/**
 * Reads a bank statement PDF in the browser and extracts the account details and transactions.
 * The file never leaves the device and is never stored — only the extracted data is used.
 *
 * Works on text-based statements (net-banking / e-mailed PDFs). The table is located by its
 * header row (Date / Narration / Debit / Credit / Balance …) and each text item is assigned to a
 * column by its x-position. Every row's running balance is re-checked so misreads are flagged.
 */

export class StatementPdfError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code; // NEED_PASSWORD | WRONG_PASSWORD | NO_TEXT | NO_TABLE | MULTI_ACCOUNT | TOO_LARGE
  }
}

const MAX_PAGES = 300;

let pdfjsPromise = null;
async function loadPdfJs() {
  if (!pdfjsPromise) {
    pdfjsPromise = Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url")
    ]).then(([lib, worker]) => {
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    });
  }
  return pdfjsPromise;
}

/* ---------------------------- PDF → text lines ---------------------------- */

/** Groups a page's text items into lines (by y) with items sorted left→right. */
export function toLines(items) {
  const lines = [];
  items
    .filter((it) => it.str && it.str.trim())
    .map((it) => ({ str: it.str.trim(), x: it.transform[4], y: it.transform[5], w: it.width || it.str.length * 4 }))
    .sort((a, b) => b.y - a.y || a.x - b.x)
    .forEach((it) => {
      const line = lines.find((l) => Math.abs(l.y - it.y) <= 2.5);
      if (line) line.items.push(it);
      else lines.push({ y: it.y, items: [it] });
    });
  lines.forEach((l) => l.items.sort((a, b) => a.x - b.x));
  return lines.sort((a, b) => b.y - a.y);
}

/** Reads all pages. Throws StatementPdfError for password / scanned / oversized files. */
export async function readPdfPages(data, { password, onProgress } = {}) {
  const pdfjs = await loadPdfJs();
  let pdf;
  try {
    pdf = await pdfjs.getDocument({ data, password: password || undefined, isEvalSupported: false }).promise;
  } catch (err) {
    if (err?.name === "PasswordException") {
      throw new StatementPdfError(err.code === 2 ? "WRONG_PASSWORD" : "NEED_PASSWORD",
        err.code === 2 ? "Wrong PDF password." : "This PDF is password protected.");
    }
    throw err;
  }
  if (pdf.numPages > MAX_PAGES) {
    throw new StatementPdfError("TOO_LARGE", `This PDF has ${pdf.numPages} pages. Please import at most ${MAX_PAGES} pages at a time (e.g. one month or quarter).`);
  }
  const pages = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const content = await page.getTextContent();
    pages.push(toLines(content.items));
    if (onProgress) onProgress(n, pdf.numPages);
  }
  if (!pages.some((lines) => lines.length > 3)) {
    throw new StatementPdfError("NO_TEXT", "This PDF looks like a scanned image, so its text can't be read. Download the statement from net-banking as a PDF (not a scan) and try again.");
  }
  return pages;
}

/* ------------------------------ value parsing ------------------------------ */

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const pad = (n) => String(n).padStart(2, "0");

/** Day-first dates as used by Indian banks → YYYY-MM-DD, or "" if not a date. */
export function parseDate(text) {
  const s = String(text || "").trim();
  let m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/);
  if (m) {
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    const month = Number(m[2]);
    const day = Number(m[1]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) return `${year}-${pad(month)}-${pad(day)}`;
  }
  m = s.match(/^(\d{1,2})[\s\-/.]*([A-Za-z]{3,4})[A-Za-z]*[\s\-/.,]*(\d{2,4})\b/);
  if (m && MONTHS[m[2].toLowerCase()]) {
    const year = m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return `${year}-${pad(MONTHS[m[2].toLowerCase()])}-${pad(Number(m[1]))}`;
  }
  return "";
}

/** Indian-formatted amount → paise (integer). "1,20,400.50" → 12040050; "45.00 Dr" → -4500 when signed. */
export function parseAmountPaise(text, { signed = false } = {}) {
  const s = String(text || "").replace(/₹|INR|Rs\.?/gi, "").trim();
  if (!s || /^[-–—]+$/.test(s)) return null;
  const m = s.match(/^\(?(-)?\s*([0-9][0-9,]*)(\.\d{1,2})?\)?\s*(cr|dr)?\.?$/i);
  if (!m) return null;
  const rupees = Number(m[2].replace(/,/g, ""));
  const paise = Math.round(rupees * 100 + (m[3] ? Number(`0${m[3]}`) * 100 : 0));
  const negative = m[1] === "-" || s.startsWith("(") || (signed && m[4] && m[4].toLowerCase() === "dr");
  return negative ? -paise : paise;
}

/* ----------------------------- table detection ----------------------------- */

// Header words → column keys. Checked in order (more specific first). Trailing "(Dr)", "(INR)" etc. are allowed.
const UNIT = String.raw`(\s*\(?\s*(dr|cr|inr|rs\.?|₹)\s*\)?)*`;
export const COLUMN_WORDS = [
  ["valueDate", new RegExp(String.raw`^value\s*(dt\.?|date)$`, "i")],
  ["sno", /^(s\.?\s*no\.?|sl\.?\s*no\.?|sr\.?\s*no\.?|sno|#)$/i],
  ["txnId", /^(txn|tran|transaction)\.?\s*(id|ref(erence)?\s*no\.?)$/i],
  ["ref", /^(chq\.?|cheque|ref\.?|reference|instrument)(\s*[./]?\s*(ref\.?|reference|no\.?|number|chq\.?|cheque|id))*\.?$/i],
  ["time", /^(txn\.?\s*)?time$/i],
  ["date", new RegExp(String.raw`^(txn\.?|tran\.?|trans\.?|transaction|post(ing)?|book(ing)?)?\s*date(\s*(&|and)?\s*time)?$`, "i")],
  ["description", /^(narration|description|particulars|details|transaction\s*(details|remarks|description|particulars)|remarks)$/i],
  ["debit", new RegExp(String.raw`^(withdrawals?|debits?|dr|withdrawal\s*amt\.?|debit\s*amount|withdrawal\s*amount|debit\s*amt\.?)${UNIT}$`, "i")],
  ["credit", new RegExp(String.raw`^(deposits?|credits?|cr|deposit\s*amt\.?|credit\s*amount|deposit\s*amount|credit\s*amt\.?)${UNIT}$`, "i")],
  ["amount", new RegExp(String.raw`^(txn\.?\s*|transaction\s*)?amount${UNIT}$`, "i")],
  ["type", /^(dr\s*\/\s*cr|cr\s*\/\s*dr|type|txn\.?\s*type|debit\s*\/\s*credit)$/i],
  ["branch", /^(branch|init\.?\s*br(anch)?|branch\s*code|sol\s*id)$/i],
  ["balance", new RegExp(String.raw`^(closing\s*|available\s*|running\s*)?balance${UNIT}$`, "i")]
];

/** Joins items that sit close together on one line into cells ("Txn" + "Date" → "Txn Date"). */
function toCells(items) {
  const cells = [];
  items.forEach((it) => {
    const last = cells[cells.length - 1];
    if (last && it.x - (last.x + last.w) < 6) {
      last.str += ` ${it.str}`;
      last.w = it.x + it.w - last.x;
    } else {
      cells.push({ ...it });
    }
  });
  return cells;
}

/**
 * Merges the cells of 1–3 stacked header lines by horizontal overlap, so a two-line header cell
 * like "Withdrawals" over "(Dr)" becomes one cell "Withdrawals (Dr)" with the union of both extents.
 */
function mergeHeaderLines(lines) {
  const groups = [];
  lines.forEach((line) => {
    toCells(line.items).forEach((cell) => {
      const right = cell.x + cell.w;
      const g = groups.find((grp) => cell.x < grp.right && right > grp.x);
      if (g) {
        g.parts.push({ y: line.y, str: cell.str });
        g.x = Math.min(g.x, cell.x);
        g.right = Math.max(g.right, right);
      } else {
        groups.push({ x: cell.x, right, parts: [{ y: line.y, str: cell.str }] });
      }
    });
  });
  return groups
    .map((g) => ({ x: g.x, right: g.right, str: g.parts.sort((a, b) => b.y - a.y).map((p) => p.str).join(" ").replace(/\s+/g, " ").trim() }))
    .sort((a, b) => a.x - b.x);
}

function headerColumns(cells, aliases = {}) {
  const columns = [];
  cells.forEach((cell) => {
    const text = cell.str.replace(/\s+/g, " ").trim();
    let key = Object.keys(aliases).find((k) => aliases[k].some((re) => re.test(text)));
    if (!key) key = (COLUMN_WORDS.find(([, re]) => re.test(text)) || [])[0];
    if (key && !columns.some((c) => c.key === key)) columns.push({ key, x: cell.x, right: cell.right ?? cell.x + cell.w });
  });
  return columns.sort((a, b) => a.x - b.x);
}

const isTableHeader = (keys) => keys.includes("date") && (keys.includes("balance") || (keys.includes("debit") && keys.includes("credit")) || keys.includes("amount"));

/** Finds the header, allowing it to span up to 3 stacked lines within ~14pt. */
function findHeader(lines, aliases) {
  for (let i = 0; i < lines.length; i++) {
    for (let size = 1; size <= 3 && i + size <= lines.length; size++) {
      const window = lines.slice(i, i + size);
      if (window[0].y - window[window.length - 1].y > 14) break;
      const columns = headerColumns(mergeHeaderLines(window), aliases);
      if (isTableHeader(columns.map((c) => c.key))) {
        // Swallow further stacked header fragments like "(Dr) (Cr) (INR)" — no dates or amounts in them
        let endIndex = i + size - 1;
        while (
          endIndex + 1 < lines.length &&
          lines[i].y - lines[endIndex + 1].y <= 14 &&
          !lines[endIndex + 1].items.some((it) => parseDate(it.str) || parseAmountPaise(it.str) !== null)
        ) endIndex++;
        const merged = headerColumns(mergeHeaderLines(lines.slice(i, endIndex + 1)), aliases);
        // Keep the extension only if it still recognises every column the shorter header did
        const keepsAll = columns.every((c) => merged.some((m) => m.key === c.key));
        return keepsAll ? { index: i, endIndex, columns: merged } : { index: i, endIndex: i + size - 1, columns };
      }
    }
  }
  return null;
}

/** Column bands: boundary between two columns is midway between one header's end and the next's start. */
function columnFor(item, columns) {
  const center = item.x + item.w / 2;
  for (let i = 0; i < columns.length; i++) {
    const next = columns[i + 1];
    const boundary = next ? (columns[i].right + next.x) / 2 : Infinity;
    if (center < boundary) return columns[i].key;
  }
  return columns[columns.length - 1].key;
}

/* ---------------------------- account details ---------------------------- */

export const IFSC_BANKS = {
  TMBL: "Tamilnad Mercantile Bank", SBIN: "State Bank of India", HDFC: "HDFC Bank", ICIC: "ICICI Bank",
  UTIB: "Axis Bank", CNRB: "Canara Bank", IOBA: "Indian Overseas Bank", IDIB: "Indian Bank",
  KVBL: "Karur Vysya Bank", CIUB: "City Union Bank", PUNB: "Punjab National Bank", BARB: "Bank of Baroda",
  UBIN: "Union Bank of India", KKBK: "Kotak Mahindra Bank", YESB: "Yes Bank", IDFB: "IDFC First Bank",
  FDRL: "Federal Bank", SIBL: "South Indian Bank", LAVB: "Lakshmi Vilas Bank", BKID: "Bank of India",
  MAHB: "Bank of Maharashtra", INDB: "IndusInd Bank", CSBK: "CSB Bank", DCBL: "DCB Bank", ESFB: "Equitas Small Finance Bank",
  IBKL: "IDBI Bank", AUBL: "AU Small Finance Bank", BDBL: "Bandhan Bank", JAKA: "Jammu & Kashmir Bank",
  KARB: "Karnataka Bank", PSIB: "Punjab & Sind Bank", RATN: "RBL Bank", SCBL: "Standard Chartered Bank",
  UCBA: "UCO Bank", CBIN: "Central Bank of India", TJSB: "TJSB Sahakari Bank", UJVN: "Ujjivan Small Finance Bank",
  SURY: "Suryoday Small Finance Bank", DLXB: "Dhanlaxmi Bank", NTBL: "Nainital Bank", HSBC: "HSBC", CITI: "Citibank"
};

function extractAccount(allText) {
  const accountNumbers = new Set();
  const re = /(?:a\/?c\.?|account)\s*(?:no\.?|number|num|#)?\s*[:\-]?\s*([0-9Xx*]{6,20})\b/gi;
  let m;
  while ((m = re.exec(allText))) {
    const digits = m[1].replace(/[^0-9]/g, "");
    if (digits.length >= 4) accountNumbers.add(m[1].toUpperCase());
  }
  const ifsc = (allText.match(/\b([A-Z]{4}0[A-Z0-9]{6})\b/) || [])[1] || "";
  const holder = ((allText.match(/(?:account\s*holder\s*name|customer\s*name|account\s*name|name)\s*[:\-]\s*([A-Za-z0-9 .&'()-]{3,60}?)(?:\s{2,}|\n|$)/i) || [])[1] || "").trim();
  return { numbers: [...accountNumbers], ifsc, holder, bankName: (ifsc && IFSC_BANKS[ifsc.slice(0, 4)]) || "" };
}

function extractPeriod(allText) {
  const d = "(\\d{1,2}[\\/\\-.]\\d{1,2}[\\/\\-.]\\d{2,4}|\\d{1,2}[\\s\\-]?[A-Za-z]{3,9}[\\s\\-,]*\\d{2,4})";
  const m = allText.match(new RegExp(`(?:from|period)\\s*[:\\-]?\\s*${d}\\s*(?:to|till|-|–)\\s*${d}`, "i"));
  return m ? { from: parseDate(m[1]), to: parseDate(m[2]) } : { from: "", to: "" };
}

/** Account details straight from the PDF text (works even when the table can't be read). */
export function detectAccount(pages) {
  const allText = pages.flat().map((l) => l.items.map((i) => i.str).join("  ")).join("\n");
  const a = extractAccount(allText);
  return { number: a.numbers[0] || "", ifsc: a.ifsc, bankName: a.bankName, holder: a.holder };
}

/* ------------------------------ bank summary ------------------------------ */

const SUMMARY_KEYS = [
  ["debitCount", /^(dr|debit|withdrawal)s?\s*(count|nos?\.?)$|^no\.?\s*of\s*(debits?|withdrawals?)$/i],
  ["creditCount", /^(cr|credit|deposit)s?\s*(count|nos?\.?)$|^no\.?\s*of\s*(credits?|deposits?)$/i],
  ["debitTotal", /^(total\s*)?(debits?|withdrawals?)(\s*amount)?$/i],
  ["creditTotal", /^(total\s*)?(credits?|deposits?)(\s*amount)?$/i],
  ["closing", /^closing\s*balance$/i]
];

/** Reads a "Dr Count | Cr Count | Debits | Credits" style block (header line + values line), if present. */
function extractSummary(lines, afterIndex) {
  for (let i = afterIndex; i < lines.length - 1; i++) {
    const cells = toCells(lines[i].items).map((c) => ({ ...c, center: c.x + c.w / 2 }));
    const keyed = cells
      .map((c) => ({ c, key: (SUMMARY_KEYS.find(([, re]) => re.test(c.str.trim())) || [])[0] }))
      .filter((k) => k.key);
    if (keyed.length < 2) continue;
    const values = toCells(lines[i + 1].items).map((c) => ({ str: c.str.trim(), center: c.x + c.w / 2 }));
    const summary = {};
    keyed.forEach(({ c, key }) => {
      const nearest = values.reduce((best, v) => (!best || Math.abs(v.center - c.center) < Math.abs(best.center - c.center) ? v : best), null);
      if (!nearest) return;
      if (key.endsWith("Count")) summary[key] = Number(nearest.str.replace(/[^0-9]/g, ""));
      else {
        const paise = parseAmountPaise(nearest.str);
        if (paise !== null) summary[key] = Math.abs(paise);
      }
    });
    if (Object.keys(summary).length >= 2) return summary;
  }
  return null;
}

/* ------------------------------ main parser ------------------------------ */

// A value-date column whose header wraps onto two lines can be read as part of the narration — drop it
const LEADING_DATE = /^(\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}|\d{1,2}[\s\-]?[A-Za-z]{3}[\s\-]?\d{2,4})(\s+\d{1,2}:\d{2}(:\d{2})?)?\s+/;
function stripLeadingDates(text) {
  let t = String(text || "").trim();
  while (LEADING_DATE.test(t)) t = t.replace(LEADING_DATE, "");
  return t;
}
const TIME = /\b(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?\b/i;
function parseTime(text) {
  const m = String(text || "").match(TIME);
  if (!m) return "";
  let h = Number(m[1]);
  if (m[4]) h = (h % 12) + (m[4].toLowerCase() === "pm" ? 12 : 0);
  return `${pad(h)}:${m[2]}${m[3] ? `:${m[3]}` : ""}`;
}

const SKIP_LINE = /(page\s*(no\.?)?\s*[.:]?\s*\d+\s*((of|\/)\s*\d+)?|this is a (system|computer) generated|generated through|grand\s*total|carried\s*forward|\bc\/f\b)/i;
const TOTAL_ROW = /^\s*(total|grand\s*total)\b/i;
const OPENING_LINE = /(opening\s*balance|balance\s*b\/?f|brought\s*forward|\bb\/f\b)/i;
const TABLE_END = /(statement\s*summary|^\s*summary\b|legends?\s*for|important\s*information|closing\s*balance|end\s*of\s*statement)/i;
const BAND_GAP = 16; // max vertical gap (pt) between lines of the same transaction

function cellsFor(line, columns) {
  const cols = {};
  line.items.forEach((item) => {
    const key = columnFor(item, columns);
    cols[key] = cols[key] ? `${cols[key]} ${item.str}` : item.str;
  });
  return cols;
}

function buildRow(band) {
  const get = (k) => band[k] || "";
  let debitPaise = Math.abs(parseAmountPaise(get("debit")) || 0);
  let creditPaise = Math.abs(parseAmountPaise(get("credit")) || 0);
  if (!debitPaise && !creditPaise && band.amount) {
    const raw = get("amount");
    const amt = parseAmountPaise(raw, { signed: true });
    const kind = `${get("type")} ${raw}`.toLowerCase();
    const isDebit = /\b(dr|debit|withdrawal)\b/.test(kind) || (amt !== null && amt < 0 && !/\b(cr|credit|deposit)\b/.test(kind));
    if (amt !== null) {
      if (isDebit) debitPaise = Math.abs(amt);
      else creditPaise = Math.abs(amt);
    }
  }
  return {
    date: band.date,
    time: band.time || parseTime(get("dateRaw")) || parseTime(get("time")),
    description: stripLeadingDates(get("description")).replace(/\s+/g, " ").trim(),
    ref: get("ref").replace(/\s+/g, " ").trim() === "-" ? "" : get("ref").replace(/\s+/g, " ").trim(),
    debitPaise,
    creditPaise,
    balancePaise: parseAmountPaise(get("balance"), { signed: true })
  };
}

/**
 * Parses text lines (from readPdfPages) into account details and transactions.
 * Pure function — usable in Node tests. `profile` (see bankProfiles.js) may add header aliases
 * or a saved manual column mapping for a bank.
 */
export function parseStatement(pages, { profile = null, bank = null } = {}) {
  if (typeof profile?.extract === "function") return profile.extract(pages, { bank });
  const allLines = pages.flat();
  const allText = allLines.map((l) => l.items.map((i) => i.str).join("  ")).join("\n");
  const aliases = profile?.aliases || {};

  const account = extractAccount(allText);
  const distinctTails = new Set(account.numbers.map((n) => n.replace(/[^0-9]/g, "").slice(-4)));
  if (distinctTails.size > 1) {
    throw new StatementPdfError("MULTI_ACCOUNT", "This PDF seems to contain more than one account. Please download and import each account's statement separately.");
  }
  const cardMarkers = [/minimum\s*amount\s*due/i, /payment\s*due\s*date/i, /credit\s*limit/i, /total\s*amount\s*due/i, /card\s*(no\.?|number)\s*[:\-]/i];
  if (cardMarkers.filter((re) => re.test(allText)).length >= 2) {
    throw new StatementPdfError("NO_TABLE", "This looks like a credit-card statement. Only bank account statements can be imported.");
  }

  let columns = null;
  let opening = null;
  const bands = [];
  let summary = null;
  const bandMode = profile?.bandMode || "below";

  pages.forEach((lines) => {
    const found = findHeader(lines, aliases);
    if (found) columns = found.columns;
    else if (!columns && profile?.columns?.length) columns = profile.columns;
    if (!columns) return;

    // 1. Classify the page's table lines: anchors (a date in the Date column) and the rest
    const start = found ? found.endIndex + 1 : 0;
    const entries = [];
    for (let i = start; i < lines.length; i++) {
      const line = lines[i];
      const text = line.items.map((it) => it.str).join(" ");
      if (i > start && findHeader([line], aliases)) continue; // repeated header line
      const cols = cellsFor(line, columns);
      const date = parseDate(cols.date);
      if (!date && TOTAL_ROW.test(text)) {
        // A "Total" row under the Debit / Credit columns is the bank's own cross-check
        const debitTotal = parseAmountPaise(cols.debit);
        const creditTotal = parseAmountPaise(cols.credit);
        if (debitTotal !== null || creditTotal !== null) {
          summary = summary || {};
          if (debitTotal !== null) summary.debitTotal = Math.abs(debitTotal);
          if (creditTotal !== null) summary.creditTotal = Math.abs(creditTotal);
        }
        continue;
      }
      if (!date && TABLE_END.test(text)) {
        summary = { ...(extractSummary(lines, i) || {}), ...(summary || {}) };
        break;
      }
      if (OPENING_LINE.test(text)) {
        const bal = parseAmountPaise(cols.balance, { signed: true });
        if (bal !== null && opening === null) opening = bal;
        continue;
      }
      if (SKIP_LINE.test(text)) continue;
      entries.push({ y: line.y, cols, date, line });
    }

    // 2. Learn where each column's text really starts from the transaction rows. Headers are often
    //    centred over wide columns ("Narration"), so a short wrapped line like "1234-UPI" would be
    //    nearer the Date header; placing text by its left edge against these starts fixes that.
    const dataStart = {};
    entries.filter((e) => e.date).forEach((e) => {
      e.line.items.forEach((item) => {
        const key = columnFor(item, columns);
        if (dataStart[key] === undefined || item.x < dataStart[key]) dataStart[key] = item.x;
      });
    });
    const starts = Object.entries(dataStart).sort((p, q) => p[1] - q[1]);
    const isValue = (str) => parseDate(str) || parseAmountPaise(str) !== null;
    // Text on the date lines too: e.g. HDFC "UPI-NAME" at the very start of the narration column
    entries.filter((e) => e.date).forEach((e) => {
      const cols = {};
      e.line.items.forEach((item) => {
        let key = columnFor(item, columns);
        if (!isValue(item.str) && starts.length) {
          const byLeftEdge = starts.filter(([, x]) => x <= item.x + 2).pop();
          if (byLeftEdge && byLeftEdge[0] !== "date") key = byLeftEdge[0];
        }
        cols[key] = cols[key] ? `${cols[key]} ${item.str}` : item.str;
      });
      e.cols = cols;
    });
    entries.filter((e) => !e.date).forEach((e) => {
      const cols = {};
      e.line.items.forEach((item) => {
        let key = columnFor(item, columns);
        if (!isValue(item.str) && starts.length) {
          const byLeftEdge = starts.filter(([, x]) => x <= item.x + 2).pop();
          if (byLeftEdge) key = byLeftEdge[0];
        }
        cols[key] = cols[key] ? `${cols[key]} ${item.str}` : item.str;
      });
      e.cols = cols;
    });
    // How wrapped narration lines are joined: banks that cut text mid-word when the column is full
    // ("HDF" / "C/UPI") join directly; otherwise (word-wrapped / unknown) keep a space.
    const joiner = profile?.narrationWrap === "cut" ? "" : " ";

    // 3. Group the other lines with their transaction
    const anchors = entries.filter((e) => e.date).map((e) => ({ ...e, parts: [e] }));
    if (!anchors.length) return;
    const others = entries.filter((e) => !e.date);

    if (bandMode === "around") {
      // Rows whose cells are vertically centred: text sits above AND below the date line (e.g. TMB)
      others.forEach((e) => {
        const nearest = anchors.reduce((best, a) => (Math.abs(a.y - e.y) < Math.abs(best.y - e.y) ? a : best));
        if (Math.abs(nearest.y - e.y) <= BAND_GAP) nearest.parts.push(e);
      });
    } else {
      // "below": wrapped text follows the date line (HDFC, IDBI, SBI…). The allowed gap adapts to the
      // statement's own line spacing, so tall rows (HDFC ≈ 17pt) and tight rows (IDBI ≈ 4pt) both work,
      // while letterheads and footers far away are never attached.
      // Line pitch = the most common gap between lines (ignoring sub-line jitter < 3pt)
      const counts = new Map();
      entries.slice(1).forEach((e, i) => {
        const g = Math.round(entries[i].y - e.y);
        if (g >= 3 && g <= 40) counts.set(g, (counts.get(g) || 0) + 1);
      });
      const pitch = [...counts.entries()].sort((p, q) => q[1] - p[1] || p[0] - q[0])[0]?.[0] || BAND_GAP;
      const maxGap = Math.max(BAND_GAP, pitch * 1.3);
      let current = null;
      let lastY = null;
      entries.forEach((e) => {
        if (e.date) {
          current = anchors.find((a) => a.y === e.y);
          lastY = e.y;
          return;
        }
        if (current && lastY - e.y <= maxGap) {
          current.parts.push(e);
          lastY = e.y;
        } else if (!current) {
          // values printed just above the first row (vertically centred cell) belong to it
          const first = anchors[0];
          if (e.y - first.y <= BAND_GAP) first.parts.push(e);
        }
      });
    }

    // 4. Merge each transaction's parts top-to-bottom: narration joined, other cells filled once
    anchors.forEach((a) => {
      const band = { date: a.date, dateRaw: a.cols.date };
      a.parts.sort((p, q) => q.y - p.y).forEach((part) => {
        Object.entries(part.cols).forEach(([k, v]) => {
          if (k === "description") {
            band.description = band.description ? `${band.description}${joiner}${v}` : v;
          }
          else if (k === "date") return;
          else if (!band[k]) band[k] = v;
        });
      });
      // the anchor's own values win over values found on neighbouring lines
      Object.entries(a.cols).forEach(([k, v]) => { if (k !== "description" && k !== "date") band[k] = v; });
      bands.push(band);
    });
  });

  if (!columns) {
    const err = new StatementPdfError("NO_TABLE", "Couldn't find the transactions table (Date / Description / Debit / Credit / Balance) in this PDF.");
    err.diagnostics = buildDiagnostics(pages);
    throw err;
  }

  const rows = bands.map((b) => buildRow(b)).filter((r) => r.debitPaise || r.creditPaise);
  if (!rows.length) {
    const err = new StatementPdfError("NO_TABLE", "No transactions were found in this PDF.");
    err.diagnostics = buildDiagnostics(pages);
    throw err;
  }

  // Chronological order (newest-first statements are reversed); seq keeps the bank's same-day order
  const newestFirst = profile?.newestFirst ?? (rows.length > 1 && `${rows[0].date} ${rows[0].time}` > `${rows[rows.length - 1].date} ${rows[rows.length - 1].time}`);
  if (newestFirst) rows.reverse();
  rows.forEach((r, i) => { r.seq = i + 1; });

  const hasBalance = rows.every((r) => r.balancePaise !== null);
  let prev = opening;
  if (hasBalance && prev === null) prev = rows[0].balancePaise + rows[0].debitPaise - rows[0].creditPaise;
  let flagged = 0;
  rows.forEach((r) => {
    if (hasBalance) {
      r.flag = prev - r.debitPaise + r.creditPaise !== r.balancePaise;
      if (r.flag) flagged++;
      prev = r.balancePaise;
    } else {
      r.flag = false;
    }
  });

  // Cross-check against the bank's own summary block when the statement has one
  let summaryCheck = null;
  if (summary) {
    const got = {
      debitCount: rows.filter((r) => r.debitPaise).length,
      creditCount: rows.filter((r) => r.creditPaise).length,
      debitTotal: rows.reduce((s, r) => s + r.debitPaise, 0),
      creditTotal: rows.reduce((s, r) => s + r.creditPaise, 0),
      closing: hasBalance ? rows[rows.length - 1].balancePaise : null
    };
    const mismatches = Object.keys(summary).filter((k) => got[k] !== null && got[k] !== undefined && got[k] !== summary[k]);
    summaryCheck = { ok: mismatches.length === 0, bank: summary, read: got, mismatches };
  }

  const periodFromText = extractPeriod(allText);
  return {
    bank: bank || null,
    profileUsed: profile?.name || (profile?.columns ? "saved column layout" : "generic"),
    account: { number: account.numbers[0] || "", ifsc: account.ifsc, bankName: account.bankName || bank?.name || "", holder: account.holder },
    period: { from: periodFromText.from || rows[0].date, to: periodFromText.to || rows[rows.length - 1].date },
    hasBalance,
    opening: hasBalance ? (opening ?? rows[0].balancePaise + rows[0].debitPaise - rows[0].creditPaise) : null,
    closing: hasBalance ? rows[rows.length - 1].balancePaise : null,
    rows,
    flagged,
    summaryCheck
  };
}

/* ------------------------- transaction identity ------------------------- */

const normaliseText = (s) => String(s || "").toUpperCase().replace(/\s+/g, " ").trim();

/**
 * A stable identity per row, used (with the account id) as the duplicate-proof key:
 *   date | time (when printed) | debit | credit | balance (or description when there's no balance)
 * plus an occurrence number (#1, #2 …) for rows that are otherwise identical within the statement —
 * e.g. two ₹1,00,000 debits on the same day that leave the same balance because a credit landed between them.
 * An overlapping statement numbers such rows the same way, so they still match across imports.
 */
export function rowIdentities(rows, hasBalance) {
  const seen = new Map();
  return rows.map((row) => {
    let base = `${row.date}|${row.time || ""}|${row.debitPaise}|${row.creditPaise}`;
    base += hasBalance && row.balancePaise !== null ? `|${row.balancePaise}` : `|${normaliseText(row.description)}`;
    const n = (seen.get(base) || 0) + 1;
    seen.set(base, n);
    return `${base}|#${n}`;
  });
}

/* ------------------------- fallback: manual mapping ------------------------- */

/**
 * For layouts the reader can't recognise: clusters the x-positions of lines that contain a date into
 * candidate columns, with a few sample values each, so the user can say which column is which.
 */
export function detectColumnCandidates(pages) {
  const sampleLines = pages.flat().filter((l) => l.items.some((it) => parseDate(it.str))).slice(0, 40);
  const clusters = [];
  sampleLines.forEach((line) => {
    toCells(line.items).forEach((cell) => {
      const right = cell.x + cell.w;
      const c = clusters.find((k) => cell.x < k.right + 4 && right > k.x - 4);
      if (c) {
        c.x = Math.min(c.x, cell.x);
        c.right = Math.max(c.right, right);
        if (c.samples.length < 4) c.samples.push(cell.str);
        c.hits++;
      } else {
        clusters.push({ x: cell.x, right, samples: [cell.str], hits: 1 });
      }
    });
  });
  return clusters.filter((c) => c.hits >= Math.min(3, sampleLines.length)).sort((a, b) => a.x - b.x);
}

/** Masked layout info for support: positions and shapes only — digits become #, long words are cut. */
export function buildDiagnostics(pages) {
  const mask = (s) => String(s).replace(/\d/g, "#").replace(/[A-Za-z]{4,}/g, (w) => `${w.slice(0, 3)}…`);
  const lines = pages[0] || [];
  return {
    pages: pages.length,
    lines: lines.slice(0, 60).map((l) => `${l.y.toFixed(0)}| ${l.items.map((i) => `${i.x.toFixed(0)}:${mask(i.str)}`).join("  ")}`)
  };
}

/** Convenience: read + parse. */
export async function readStatementPdf(data, options = {}) {
  const pages = await readPdfPages(data, options);
  return parseStatement(pages, { profile: options.profile });
}
